import React, { useRef } from 'react'
import {
  Control,
  Controller,
  FieldErrors,
  useFieldArray,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
import { msgid, ngettext, t } from 'ttag'
import { ReactComponent as RemoveIcon } from '../../../../assets/v3/icons/remove.svg'
import { ReactComponent as AddRoundedIcon } from '../../../../assets/v3/icons/add-rounded.svg'
import DragHandleIcon from '@material-ui/icons/DragHandle'
import { Box, IconButton, TextField } from '@material-ui/core'
import { activityTypes, FormData } from '../../ChallengeAdmin'
import ChallengePointsUnitField from './ChallengePointsUnitField'
import { CustomTypeCategory, customUnits } from '../../../../services/CustomTypes'
import ChallengePointsActivityTypeField from './ChallengePointsActivityTypeField'
import ChallengePointsAmountField from './ChallengePointsAmountField'
import { CustomType, ProgressType } from '../../../../services/api.types'
import ChallengePointsCustomUnitField from './ChallengePointsCustomUnitField'
import { isAutomatedType, isDistanceType, isSimpleDayType } from '../../../../common/helpers/challenges'
import ChallengePointsSimpleManualEntryField from './ChallengePointsSimpleManualEntryField'
import ChallengePointsDailyMaxField from './ChallengePointsDailyMaxField'
import { darkBlue } from '../../../../styles/material-ui-theme-v2'
import { ReactComponent as InfoIcon } from '../../../../assets/v2/icons/icon-info-gray.svg'
import { dailyMaxLabel } from '../../../ChallengeInfo/ChallengeInfo'
import ChallengePointsTooltip from './ChallengePointsTooltip'

interface ChallengePointsProps {
  control: Control<FormData, any>
  watch: UseFormWatch<FormData>
  register: UseFormRegister<FormData>
  setValue: UseFormSetValue<FormData>
  errors: FieldErrors<FormData>
}

export type PointsLogicId = string

export interface PointsLogicTemplate {
  category: CustomTypeCategory
  value: number
  unit: CustomType
  points: number
  customActivityName?: string
  customUnitName?: string
  simpleManualEntry?: boolean
  dailyGoal?: number
  // description: string
}

export interface PointsLogic extends PointsLogicTemplate {
  id: PointsLogicId
}

export const ignoreCategoriesForPoints = [
  ProgressType.POINTS,
  ProgressType.CUSTOM,
  ProgressType.ALL,
  ProgressType.CALORIES,
]

const getNextUnusedActivityType = (fields: any[]) => {
  const sortedActivityTypes = activityTypes.sort((a, b) => {
    return a.usesIntegration === b.usesIntegration ? 0 : a.usesIntegration ? -1 : 1
  })
  const unusedCategories: any[] = sortedActivityTypes.filter(
    (activityType) =>
      !fields.some(
        (field) =>
          field.category === activityType.value ||
          // @ts-ignore
          ignoreCategoriesForPoints.includes(activityType.value),
      ),
  )
  return unusedCategories[0]?.value || CustomTypeCategory.CUSTOM_SIMPLE
}

const getFirstUnitForCategory = (category: CustomTypeCategory) => {
  if ([CustomTypeCategory.CUSTOM_SIMPLE, CustomTypeCategory.CUSTOM_WITH_AMOUNT].includes(category)) {
    return CustomType.CUSTOM
  }
  return customUnits.find((c) => c.category === category)?.id
}

export const checkPointsError = (errors: any, index: number, name: string) => {
  // @ts-ignore
  return errors.challengePoints?.[index]?.[name]
  //return !!errors.challengePoints?.[index]?.[name]
}

export default function ChallengePoints({ control, setValue, watch, errors }: ChallengePointsProps) {
  const { fields, append, move, remove, update } = useFieldArray({
    control,
    name: 'challengePoints',
  })
  const childrenRef = useRef<Record<string, (sourceIndex: number, destIndex: number) => void>>({})

  const reorder = (result: any) => {
    const { source, destination, type } = result
    if (!destination) {
      return
    }
    const sourceIndex = source.index
    const destIndex = destination.index

    if (type === 'parentContainer') {
      move(sourceIndex, destIndex)
    } else if (type === 'childContainer' && source.droppableId) {
      const reorderChild = childrenRef.current[source.droppableId]
      if (reorderChild) {
        reorderChild(sourceIndex, destIndex)
      }
    }
  }

  const handleAdd = () => {
    const category = getNextUnusedActivityType(fields)
    const unit = getFirstUnitForCategory(category)
    append({
      category,
      value: 1,
      unit,
      points: 1,
      customActivityName: '',
      customUnitName: '',
      simpleManualEntry: false,
    })
  }

  // Hack to re-render form. Refactor if you know any better way.
  const onActivityTypeChange = (index: number, event: any) => {
    move(0, 0)

    const category = event.target.value as CustomTypeCategory
    if (category === CustomTypeCategory.CUSTOM_SIMPLE) {
      setValue(`challengePoints.${index}.value`, 1)
      setValue(`challengePoints.${index}.unit`, CustomType.CUSTOM)
    } else if (category === CustomTypeCategory.CUSTOM_WITH_AMOUNT) {
      setValue(`challengePoints.${index}.unit`, CustomType.CUSTOM)
    } else {
      setValue(`challengePoints.${index}.unit`, getFirstUnitForCategory(category))
    }

    if (isSimpleDayType(category)) {
      setValue(`challengePoints.${index}.value`, 1)
    }
  }

  const manualEntryEnabled = watch('manualEntryEnabled')

  return (
    <>
      <DragDropContext onDragEnd={reorder}>
        <Droppable droppableId="parent" type="parentContainer">
          {(provided) => (
            <ul
              ref={provided.innerRef}
              {...provided.droppableProps}
              style={{ listStyle: 'none', padding: 0, margin: 0 }}
            >
              {fields.map((item, index) => {
                const units = customUnits.filter((unit, keyIndex) => unit.category === item.category)
                const isActivityIndex = item.category === ProgressType.ALL
                const isSimpleCustom = item.category === CustomTypeCategory.CUSTOM_SIMPLE
                const isCustomWithAmount = item.category === CustomTypeCategory.CUSTOM_WITH_AMOUNT
                const onlyOneUnit = units.length == 1 || isActivityIndex || isSimpleCustom
                const value = watch(`challengePoints.${index}.value`)
                const unit = onlyOneUnit
                  ? isActivityIndex
                    ? '%'
                    : isSimpleCustom || units[0].text === t`days`
                    ? ngettext(msgid`day`, `days`, Number(value) || 2)
                    : units[0].text
                  : undefined
                return (
                  <Draggable key={item.id} draggableId={item.id} index={index}>
                    {(provided, { isDragging }) => (
                      <li ref={provided.innerRef} {...provided.draggableProps}>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: 10,
                          }}
                        >
                          {/*<div style={{ paddingTop: 15 }}>{index + 1}.</div>*/}
                          <div style={{ flexGrow: 1 }}>
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'row',
                                /*gap: 10,*/
                                marginRight: fields.length == 1 ? 48 : 0,
                              }}
                            >
                              <ChallengePointsActivityTypeField
                                name={`challengePoints.${index}.category`}
                                onActivityTypeChange={onActivityTypeChange}
                                fields={fields}
                                index={index}
                                {...{ control }}
                              />

                              {fields.length > 1 && (
                                <IconButton
                                  style={{ flexShrink: 0, marginBottom: '10px', position: 'relative', right: '-10px' }}
                                  aria-label="delete"
                                  onClick={() => remove(index)}
                                >
                                  <RemoveIcon
                                    style={{
                                      width: 24,
                                      height: 24,
                                    }}
                                  />
                                </IconButton>
                              )}
                            </div>

                            <div style={{ marginRight: '48px' }}>
                              {[CustomTypeCategory.CUSTOM_SIMPLE, CustomTypeCategory.CUSTOM_WITH_AMOUNT].includes(
                                item.category,
                              ) && (
                                <Controller
                                  name={`challengePoints.${index}.customActivityName`}
                                  rules={{
                                    required: true,
                                  }}
                                  control={control}
                                  render={({ field: { ref, ...otherField } }) => (
                                    <TextField
                                      style={{ marginTop: 0, width: '100%' }}
                                      label={t`Enter activity name`}
                                      margin="dense"
                                      error={checkPointsError(errors, index, 'customActivityName')}
                                      variant="filled"
                                      InputProps={{
                                        disableUnderline: true,
                                      }}
                                      {...otherField}
                                    />
                                  )}
                                />
                              )}
                              {/*{![CustomTypeCategory.CUSTOM_SIMPLE, CustomTypeCategory.CUSTOM_WITH_AMOUNT].includes(
                                item.category,
                              ) && (
                                <Controller
                                  name={`challengePoints.${index}.description`}
                                  control={control}
                                  render={({ field: { ref, ...otherField } }) => (
                                    <TextField
                                      style={{ marginTop: 0, width: '100%' }}
                                      label={t`Description`}
                                      margin="dense"
                                      variant="filled"
                                      InputProps={{
                                        disableUnderline: true,
                                      }}
                                      {...otherField}
                                    />
                                  )}
                                />
                              )}*/}
                            </div>

                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'row',
                                gap: 10,
                                marginRight: '48px',
                              }}
                            >
                              <div style={{ flex: 1 }}>
                                <ChallengePointsAmountField
                                  name={`challengePoints.${index}.value`}
                                  label={t`Value`}
                                  max={100000}
                                  unit={unit}
                                  disabled={
                                    item.category === CustomTypeCategory.CUSTOM_SIMPLE || isSimpleDayType(item.category)
                                  }
                                  error={checkPointsError(errors, index, 'value')}
                                  isDistance={isDistanceType(item.unit)}
                                  {...{ control }}
                                />
                              </div>
                              {item.category === CustomTypeCategory.CUSTOM_WITH_AMOUNT && (
                                <div style={{ flex: 1 }}>
                                  <ChallengePointsCustomUnitField
                                    name={`challengePoints.${index}.customUnitName`}
                                    category={item.category}
                                    error={checkPointsError(errors, index, 'customUnitName')}
                                    {...{ control }}
                                  />
                                </div>
                              )}
                              {!onlyOneUnit && !isCustomWithAmount && (
                                <div style={{ flex: 1 }}>
                                  <ChallengePointsUnitField
                                    name={`challengePoints.${index}.unit`}
                                    category={item.category}
                                    {...{ control }}
                                  />
                                </div>
                              )}
                              <div
                                className="DragHandle"
                                style={{ display: 'flex', alignItems: 'center', marginBottom: '10px', height: '50px' }}
                                {...provided.dragHandleProps}
                              >
                                <DragHandleIcon />
                              </div>
                              {/*minWidth: '75px', maxWidth: '100px',*/}
                              <div style={{ flex: 1 }}>
                                <ChallengePointsAmountField
                                  name={`challengePoints.${index}.points`}
                                  label={t`Points`}
                                  max={100}
                                  error={checkPointsError(errors, index, 'points')}
                                  {...{ control }}
                                />
                              </div>
                            </div>

                            <div style={{ display: 'flex', gap: '10px' }}>
                              {![CustomTypeCategory.CUSTOM_SIMPLE, CustomTypeCategory.CUSTOM_WITH_AMOUNT].includes(
                                item.category,
                              ) &&
                                !isSimpleDayType(item.category) && (
                                  <>
                                    {(manualEntryEnabled || !isAutomatedType(item.unit)) && (
                                      <div style={{ flexShrink: 0 }}>
                                        <ChallengePointsSimpleManualEntryField
                                          name={`challengePoints.${index}.simpleManualEntry`}
                                          dailyGoalName={`challengePoints.${index}.dailyGoal`}
                                          setValue={setValue}
                                          watch={watch}
                                        />
                                      </div>
                                    )}
                                    <div style={{ flex: 1 }}>
                                      <ChallengePointsDailyMaxField
                                        name={`challengePoints.${index}.simpleManualEntry`}
                                        dailyGoalFieldName={`challengePoints.${index}.dailyGoal`}
                                        unitFieldName={`challengePoints.${index}.unit`}
                                        control={control}
                                        watch={watch}
                                        dailyGoalError={checkPointsError(errors, index, 'dailyGoal')}
                                      />
                                    </div>
                                    <div
                                      style={{
                                        marginRight: '22px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        paddingBottom: '10px',
                                      }}
                                    >
                                      <ChallengePointsTooltip
                                        title={`${dailyMaxLabel} = ${t`Threshold where surpassing does not increase points.`}${
                                          manualEntryEnabled || !isAutomatedType(item.unit)
                                            ? ' ' +
                                              t`If one-click manual entry is enabled, users can easily mark the daily max as done.`
                                            : ''
                                        }`}
                                        placement="top"
                                        enterTouchDelay={50}
                                        leaveTouchDelay={3000}
                                        arrow
                                      >
                                        <InfoIcon />
                                      </ChallengePointsTooltip>
                                    </div>
                                  </>
                                )}
                            </div>
                          </div>
                        </div>

                        <div
                          style={{
                            padding: '6px 0 16px',
                          }}
                        >
                          <div
                            style={{
                              height: '1px',
                              backgroundColor: darkBlue,
                              opacity: isDragging || fields.length - 1 === index ? 0 : 0.25,
                            }}
                          ></div>
                        </div>
                      </li>
                    )}
                  </Draggable>
                )
              })}
              {provided.placeholder}
            </ul>
          )}
        </Droppable>

        {fields.length < 20 && (
          <Box display="flex" justifyContent="center">
            <IconButton aria-label="add" onClick={handleAdd} style={{ borderRadius: '10px' }}>
              <AddRoundedIcon
                style={{
                  width: 24,
                  height: 24,
                }}
              />
              <span style={{ marginLeft: '10px', fontSize: '15px', color: darkBlue }}>{t`Add activity`}</span>
            </IconButton>
          </Box>
        )}
      </DragDropContext>
    </>
  )
}
